import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ClipboardList, Plus, Edit, Save, CheckSquare,
  History, Clock, Activity, CheckCircle, Download,
  User, FileText, ChevronDown, ChevronUp, RefreshCw,
  Filter, Calendar, BarChart3, X, Star, Trophy, Timer, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { generateQAChecklistMarkdown, QA_TEST_CHECKLIST } from '../data/qaTestChecklist';
import { jsPDF } from 'jspdf';
import { Input } from '../components/ui/input';

const TestingPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('checklists');

  // Checklists state
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [checklistResults, setChecklistResults] = useState({});
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [checklistNotes, setChecklistNotes] = useState('');
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [executionStartedAt, setExecutionStartedAt] = useState(null);

  // Create checklist modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newChecklist, setNewChecklist] = useState({
    name: '', description: '', frequency: 'monthly', items: []
  });
  const [newItemText, setNewItemText] = useState('');
  const [newItemCategory, setNewItemCategory] = useState('');
  const [newItemRequired, setNewItemRequired] = useState(false);

  // Completion score dialog
  const [completionResult, setCompletionResult] = useState(null);

  // History tab state
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState({ checklist_id: 'all', user: 'all' });

  const loadChecklists = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/checklists');
      setChecklists(response.data);
    } catch (error) {
      console.error('Error loading checklists:', error);
      toast.error('Error al cargar checklists');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const params = new URLSearchParams();
      if (historyFilter.checklist_id !== 'all') params.append('checklist_id', historyFilter.checklist_id);
      if (historyFilter.user !== 'all') params.append('user', historyFilter.user);
      const response = await api.get(`/checklists/history?${params.toString()}`);
      setHistoryData(response.data);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Error al cargar historial');
    } finally {
      setHistoryLoading(false);
    }
  }, [historyFilter]);

  useEffect(() => {
    loadChecklists();
  }, [loadChecklists]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, loadHistory]);

  // Checklist functions
  const migrateProgressKeys = (items, itemResults) => {
    if (!items || !itemResults) return itemResults || {};
    const validIds = new Set(items.map(i => i.id));
    const hasStaleKeys = Object.keys(itemResults).some(k => !validIds.has(k));
    if (!hasStaleKeys) return itemResults;
    const categoryIndices = {};
    const migrationMap = {};
    for (const item of items) {
      if (!categoryIndices[item.category]) categoryIndices[item.category] = 0;
      const idx = categoryIndices[item.category]++;
      migrationMap[`fallback-${item.category}-${idx}`] = item.id;
    }
    const migrated = {};
    for (const [key, val] of Object.entries(itemResults)) {
      const newKey = migrationMap[key] || key;
      if (validIds.has(newKey)) migrated[newKey] = val;
    }
    return migrated;
  };

  const openChecklistExecution = (checklist) => {
    setSelectedChecklist(checklist);
    if (checklist.current_progress?.item_results) {
      const migrated = migrateProgressKeys(checklist.items, checklist.current_progress.item_results);
      setChecklistResults(migrated);
      setChecklistNotes(checklist.current_progress.notes || '');
      setExecutionStartedAt(checklist.current_progress.started_at || new Date().toISOString());
    } else {
      setChecklistResults({});
      setChecklistNotes('');
      setExecutionStartedAt(new Date().toISOString());
    }
    setShowChecklistModal(true);
  };

  const toggleChecklistItem = (itemId) => {
    setChecklistResults(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const handleSaveProgress = async () => {
    try {
      const response = await api.post(`/checklists/${selectedChecklist.id}/save-progress`, {
        item_results: checklistResults,
        notes: checklistNotes,
        started_at: executionStartedAt
      });
      toast.success('Progreso guardado');
      setChecklists(prev => prev.map(c => c.id === selectedChecklist.id ? response.data : c));
      setSelectedChecklist(response.data);
    } catch (error) {
      toast.error('Error al guardar progreso');
    }
  };

  const handleCompleteChecklist = async () => {
    try {
      const response = await api.post(`/checklists/${selectedChecklist.id}/complete`, {
        item_results: checklistResults,
        notes: checklistNotes,
        started_at: executionStartedAt
      });
      setShowChecklistModal(false);
      // Find the latest completion entry to show score
      const updated = response.data;
      const lastEntry = updated.completion_history?.[updated.completion_history.length - 1];
      if (lastEntry?.quality_score != null) {
        setCompletionResult(lastEntry);
      } else {
        toast.success('Checklist completado');
      }
      loadChecklists();
    } catch (error) {
      toast.error('Error al completar checklist');
    }
  };

  const getChecklistProgress = (checklist) => {
    if (!checklistResults || Object.keys(checklistResults).length === 0) return 0;
    const total = checklist.items.length;
    const validIds = new Set(checklist.items.map(i => i.id));
    const completed = Object.entries(checklistResults).filter(([key, val]) => val && validIds.has(key)).length;
    return Math.round((completed / total) * 100);
  };

  const formatFrequency = (freq) => {
    const map = { daily: 'Diario', weekly: 'Semanal', monthly: 'Mensual', quarterly: 'Trimestral', yearly: 'Anual' };
    return map[freq] || freq;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('es-MX', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  // PDF Download
  const handleDownloadQAPDF = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let y = 15;
      const primaryColor = [0, 178, 169];
      const darkColor = [30, 30, 30];
      const grayColor = [100, 100, 100];
      const lightGray = [240, 240, 240];

      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, 35, 'F');
      doc.setFillColor(255, 255, 255);
      doc.circle(pageWidth / 2, 12, 6, 'F');
      doc.setFontSize(8);
      doc.setTextColor(...primaryColor);
      doc.text('CM', pageWidth / 2, 14, { align: 'center' });
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('LISTA DE PRUEBAS QA', pageWidth / 2, 26, { align: 'center' });
      doc.setFillColor(20, 20, 20);
      doc.rect(0, 35, pageWidth, 12, 'F');
      doc.setFontSize(9);
      doc.setTextColor(200, 200, 200);
      doc.text(`Sistema de Gestion de Inventario  |  Fecha: ${new Date().toLocaleDateString('es-MX')}  |  Version: v4.4.2`, pageWidth / 2, 42, { align: 'center' });
      y = 55;

      if (QA_TEST_CHECKLIST?.categories) {
        const totalTests = QA_TEST_CHECKLIST.categories.reduce((acc, cat) => acc + cat.tests.length, 0);
        const highPriority = QA_TEST_CHECKLIST.categories.reduce((acc, cat) => acc + cat.tests.filter(t => t.priority === 'Alta').length, 0);
        const mediumPriority = QA_TEST_CHECKLIST.categories.reduce((acc, cat) => acc + cat.tests.filter(t => t.priority === 'Media').length, 0);
        doc.setFillColor(...lightGray);
        doc.roundedRect(margin, y, pageWidth - margin * 2, 18, 2, 2, 'F');
        doc.setFontSize(9);
        doc.setTextColor(...darkColor);
        doc.setFont(undefined, 'bold');
        doc.text('RESUMEN:', margin + 5, y + 6);
        doc.setFont(undefined, 'normal');
        doc.text(`Total: ${totalTests} pruebas  |  `, margin + 30, y + 6);
        doc.setFillColor(239, 68, 68);
        doc.roundedRect(margin + 70, y + 2, 25, 6, 1, 1, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.text(`Alta: ${highPriority}`, margin + 75, y + 6);
        doc.setFillColor(245, 158, 11);
        doc.roundedRect(margin + 98, y + 2, 30, 6, 1, 1, 'F');
        doc.text(`Media: ${mediumPriority}`, margin + 102, y + 6);
        doc.setFillColor(16, 185, 129);
        doc.roundedRect(margin + 131, y + 2, 30, 6, 1, 1, 'F');
        doc.text(`Baja: ${totalTests - highPriority - mediumPriority}`, margin + 137, y + 6);
        doc.setFontSize(8);
        doc.setTextColor(...grayColor);
        doc.text('Marque cada casilla al completar la prueba. Documente cualquier falla encontrada.', margin + 5, y + 14);
        y += 25;
      }

      if (QA_TEST_CHECKLIST?.categories) {
        QA_TEST_CHECKLIST.categories.forEach((category, catIdx) => {
          if (y > pageHeight - 40) { doc.addPage(); y = 20; }
          doc.setFillColor(...primaryColor);
          doc.roundedRect(margin, y, pageWidth - margin * 2, 8, 1, 1, 'F');
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(255, 255, 255);
          doc.text(`${catIdx + 1}. ${category.name.toUpperCase()}`, margin + 4, y + 5.5);
          y += 12;
          category.tests.forEach((test) => {
            const stepsHeight = test.steps.length * 4 + 15;
            if (y + stepsHeight > pageHeight - 30) { doc.addPage(); y = 20; }
            const testHeight = stepsHeight + 8;
            doc.setFillColor(250, 250, 250);
            doc.roundedRect(margin, y, pageWidth - margin * 2, testHeight, 1, 1, 'F');
            doc.setDrawColor(...grayColor);
            doc.setLineWidth(0.3);
            doc.rect(margin + 3, y + 3, 4, 4);
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(...darkColor);
            doc.text(`${test.id}:`, margin + 10, y + 6);
            doc.setFont(undefined, 'normal');
            const testName = doc.splitTextToSize(test.name, pageWidth - margin * 2 - 60);
            doc.text(testName, margin + 25, y + 6);
            const priorityColor = test.priority === 'Alta' ? [239, 68, 68] : test.priority === 'Media' ? [245, 158, 11] : [16, 185, 129];
            doc.setFillColor(...priorityColor);
            doc.roundedRect(pageWidth - margin - 20, y + 2, 17, 5, 1, 1, 'F');
            doc.setFontSize(7);
            doc.setTextColor(255, 255, 255);
            doc.text(test.priority, pageWidth - margin - 18, y + 5.5);
            let stepY = y + 11;
            doc.setFontSize(8);
            doc.setTextColor(...grayColor);
            test.steps.forEach((step, sIdx) => {
              const stepText = `${sIdx + 1}. ${step}`;
              const lines = doc.splitTextToSize(stepText, pageWidth - margin * 2 - 20);
              doc.text(lines, margin + 10, stepY);
              stepY += lines.length * 3.5;
            });
            doc.setFillColor(220, 252, 231);
            doc.roundedRect(margin + 8, stepY, pageWidth - margin * 2 - 16, 7, 1, 1, 'F');
            doc.setFontSize(7);
            doc.setTextColor(22, 101, 52);
            doc.setFont(undefined, 'bold');
            doc.text('ESPERADO:', margin + 10, stepY + 4.5);
            doc.setFont(undefined, 'normal');
            const expectedText = doc.splitTextToSize(test.expected, pageWidth - margin * 2 - 50);
            doc.text(expectedText, margin + 32, stepY + 4.5);
            y += testHeight + 3;
          });
          y += 5;
        });
      }

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setDrawColor(...primaryColor);
        doc.setLineWidth(0.5);
        doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
        doc.setFontSize(8);
        doc.setTextColor(...grayColor);
        doc.text('CM Idiomas - Sistema de Gestion de Inventario', margin, pageHeight - 10);
        doc.text(`Pagina ${i} de ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      }
      doc.save('Lista_QA_CM_Idiomas.pdf');
      toast.success('PDF descargado correctamente');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error al generar PDF');
    }
  };

  // Get unique users from history for filter
  const uniqueUsers = [...new Set(historyData.map(h => h.completed_by))].filter(Boolean);
  const uniqueChecklists = [...new Set(historyData.map(h => h.checklist_name))].filter(Boolean);

  // Create checklist
  const handleCreateChecklist = async () => {
    if (!newChecklist.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (newChecklist.items.length === 0) {
      toast.error('Agrega al menos un item');
      return;
    }
    try {
      await api.post('/checklists', newChecklist);
      toast.success('Checklist creado exitosamente');
      setShowCreateModal(false);
      setNewChecklist({ name: '', description: '', frequency: 'monthly', items: [] });
      loadChecklists();
    } catch (error) {
      toast.error('Error al crear checklist');
    }
  };

  const addItemToNewChecklist = () => {
    if (!newItemText.trim()) return;
    const category = newItemCategory.trim() || 'General';
    const newItem = {
      id: `new-${Date.now()}-${newChecklist.items.length}`,
      text: newItemText.trim(),
      category,
      required: newItemRequired
    };
    setNewChecklist(prev => ({ ...prev, items: [...prev.items, newItem] }));
    setNewItemText('');
    setNewItemRequired(false);
  };

  const removeItemFromNewChecklist = (idx) => {
    setNewChecklist(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  // Quality Score helpers
  const getScoreColor = (score) => {
    if (score == null) return 'text-muted-foreground';
    if (score >= 90) return 'text-green-400';
    if (score >= 70) return 'text-emerald-400';
    if (score >= 50) return 'text-amber-400';
    return 'text-red-400';
  };

  const getScoreBg = (score) => {
    if (score == null) return 'bg-muted/50';
    if (score >= 90) return 'bg-green-500/20';
    if (score >= 70) return 'bg-emerald-500/20';
    if (score >= 50) return 'bg-amber-500/20';
    return 'bg-red-500/20';
  };

  const getScoreLabel = (score) => {
    if (score == null) return '-';
    if (score >= 90) return 'Excelente';
    if (score >= 70) return 'Bueno';
    if (score >= 50) return 'Regular';
    return 'Bajo';
  };

  const formatDuration = (minutes) => {
    if (minutes == null) return '-';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hrs}h ${mins}m`;
  };

  const avgScore = historyData.length > 0
    ? Math.round(historyData.filter(h => h.quality_score != null).reduce((acc, h) => acc + (h.quality_score || 0), 0) / Math.max(historyData.filter(h => h.quality_score != null).length, 1))
    : null;

  return (
    <div className="space-y-6" data-testid="testing-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-primary" />
            Testeo
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Checklists de verificacion y seguimiento de pruebas QA
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleDownloadQAPDF} data-testid="download-qa-pdf">
            <Download className="h-4 w-4 mr-2" />
            Descargar Lista QA (PDF)
          </Button>
          <Button variant="outline" size="sm" onClick={() => { loadChecklists(); if (activeTab === 'history') loadHistory(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="w-full">
        <div className="inline-flex h-10 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground gap-1 w-full max-w-md">
          <button
            type="button"
            onClick={() => setActiveTab('checklists')}
            className={`flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all cursor-pointer ${
              activeTab === 'checklists' ? 'bg-background text-foreground shadow-sm' : 'hover:bg-background/50'
            }`}
            data-testid="tab-checklists"
          >
            <CheckSquare className="h-4 w-4 mr-2" />
            Checklists
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('history')}
            className={`flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all cursor-pointer ${
              activeTab === 'history' ? 'bg-background text-foreground shadow-sm' : 'hover:bg-background/50'
            }`}
            data-testid="tab-history"
          >
            <History className="h-4 w-4 mr-2" />
            Historial
          </button>
        </div>

        {/* Checklists Tab */}
        {activeTab === 'checklists' && (
          <div className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="h-5 w-5 text-primary" />
                      Checklists de Verificacion
                    </CardTitle>
                    <CardDescription>
                      Listas de verificacion internas para asegurar el correcto funcionamiento del sistema
                    </CardDescription>
                  </div>
                  {user?.role === 'superadmin' && (
                    <Button size="sm" className="btn-primary" onClick={() => setShowCreateModal(true)} data-testid="new-checklist-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Nuevo Checklist
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : checklists.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No hay checklists configurados</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {checklists.map((checklist) => {
                      const historyOpen = expandedHistoryId === checklist.id;
                      const totalItems = checklist.items?.length || 0;
                      const historyEntries = checklist.completion_history || [];
                      return (
                        <Card key={checklist.id} className="bg-card/50" data-testid={`checklist-card-${checklist.id}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="font-semibold flex items-center gap-2">
                                  {checklist.name}
                                  <Badge variant="outline" className="text-xs">
                                    {formatFrequency(checklist.frequency)}
                                  </Badge>
                                  {checklist.current_progress && (
                                    <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                                      En progreso
                                    </Badge>
                                  )}
                                </h3>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {checklist.description}
                                </p>
                                <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground flex-wrap">
                                  <span className="flex items-center gap-1">
                                    <CheckSquare className="h-3 w-3" />
                                    {totalItems} items
                                  </span>
                                  {checklist.current_progress && (
                                    <span className="flex items-center gap-1 text-amber-400">
                                      <Activity className="h-3 w-3" />
                                      {Object.entries(checklist.current_progress.item_results || {}).filter(([k, v]) => v && checklist.items?.some(i => i.id === k)).length}/{totalItems} completados
                                    </span>
                                  )}
                                  {checklist.last_completed && (
                                    <span className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      Ultimo: {formatTimestamp(checklist.last_completed)}
                                    </span>
                                  )}
                                  {historyEntries.length > 0 && (
                                    <span className="flex items-center gap-1">
                                      <History className="h-3 w-3" />
                                      {historyEntries.length} ejecucion(es)
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {historyEntries.length > 0 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setExpandedHistoryId(historyOpen ? null : checklist.id)}
                                    data-testid={`history-toggle-${checklist.id}`}
                                  >
                                    <History className="h-4 w-4 mr-1" />
                                    Historial
                                    {historyOpen ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                                  </Button>
                                )}
                                {user?.role === 'superadmin' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toast.info('Funcion de edicion en desarrollo')}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  className={checklist.current_progress ? "bg-amber-500 hover:bg-amber-600" : "btn-primary"}
                                  onClick={() => openChecklistExecution(checklist)}
                                  data-testid={`execute-checklist-${checklist.id}`}
                                >
                                  <CheckSquare className="h-4 w-4 mr-2" />
                                  {checklist.current_progress ? 'Continuar' : 'Ejecutar'}
                                </Button>
                              </div>
                            </div>

                            {/* Inline History Panel */}
                            {historyOpen && historyEntries.length > 0 && (
                              <div className="mt-4 border-t pt-4" data-testid={`history-panel-${checklist.id}`}>
                                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                                  <History className="h-4 w-4 text-primary" />
                                  Historial de Ejecuciones ({historyEntries.length})
                                </h4>
                                <div className="space-y-2 max-h-80 overflow-y-auto">
                                  {[...historyEntries].reverse().map((entry, idx) => {
                                    const completedCount = Object.values(entry.item_results || {}).filter(Boolean).length;
                                    const pct = totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0;
                                    return (
                                      <div key={entry.id || idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/40 hover:bg-muted/60 transition-colors">
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${pct === 100 ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>
                                            {pct === 100 ? <CheckCircle className="h-4 w-4" /> : `${pct}%`}
                                          </div>
                                          <div className="min-w-0">
                                            <p className="text-sm font-medium truncate">
                                              <User className="h-3 w-3 inline mr-1" />
                                              {entry.completed_by || 'Desconocido'}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                              <Clock className="h-3 w-3 inline mr-1" />
                                              {formatTimestamp(entry.completed_at)}
                                            </p>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-3 text-xs">
                                          <span className={`font-medium ${pct === 100 ? 'text-green-400' : 'text-amber-400'}`}>
                                            {completedCount}/{totalItems} items
                                          </span>
                                          <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                                            <div
                                              className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-green-500' : 'bg-amber-500'}`}
                                              style={{ width: `${pct}%` }}
                                            />
                                          </div>
                                          {entry.quality_score != null && (
                                            <span className={`flex items-center gap-1 font-bold ${getScoreColor(entry.quality_score)}`}>
                                              <Star className="h-3 w-3" />{entry.quality_score}
                                            </span>
                                          )}
                                          {entry.notes && (
                                            <span className="text-muted-foreground" title={entry.notes}>
                                              <FileText className="h-3 w-3" />
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-4 mt-4">
            {/* Summary Cards */}
            {historyData.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-blue-500" />
                      <div>
                        <p className="text-2xl font-bold">{historyData.length}</p>
                        <p className="text-xs text-muted-foreground">Total Ejecuciones</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <div>
                        <p className="text-2xl font-bold">{historyData.filter(h => h.completion_pct === 100).length}</p>
                        <p className="text-xs text-muted-foreground">Completados 100%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <User className="h-5 w-5 text-purple-500" />
                      <div>
                        <p className="text-2xl font-bold">{uniqueUsers.length}</p>
                        <p className="text-xs text-muted-foreground">Usuarios</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-amber-500" />
                      <div>
                        <p className={`text-2xl font-bold ${getScoreColor(avgScore)}`}>{avgScore ?? '-'}</p>
                        <p className="text-xs text-muted-foreground">Puntaje Promedio</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Filters */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-wrap gap-3 items-center mb-4">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Filtros:</span>
                  </div>
                  <Select value={historyFilter.checklist_id} onValueChange={(v) => setHistoryFilter(prev => ({ ...prev, checklist_id: v }))}>
                    <SelectTrigger className="w-[200px]" data-testid="history-filter-checklist">
                      <SelectValue placeholder="Todos los checklists" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los checklists</SelectItem>
                      {checklists.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={historyFilter.user} onValueChange={(v) => setHistoryFilter(prev => ({ ...prev, user: v }))}>
                    <SelectTrigger className="w-[160px]" data-testid="history-filter-user">
                      <SelectValue placeholder="Todos los usuarios" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los usuarios</SelectItem>
                      {uniqueUsers.map(u => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* History Table */}
                {historyLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : historyData.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No hay historial de ejecuciones</p>
                    <p className="text-xs mt-1">Completa un checklist para ver su registro aqui</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="history-table">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 text-sm font-medium">Fecha</th>
                          <th className="text-left py-2 px-3 text-sm font-medium">Checklist</th>
                          <th className="text-left py-2 px-3 text-sm font-medium">Usuario</th>
                          <th className="text-left py-2 px-3 text-sm font-medium">Progreso</th>
                          <th className="text-left py-2 px-3 text-sm font-medium">Items</th>
                          <th className="text-left py-2 px-3 text-sm font-medium">Puntaje</th>
                          <th className="text-left py-2 px-3 text-sm font-medium">Duracion</th>
                          <th className="text-left py-2 px-3 text-sm font-medium">Notas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyData.map((entry) => (
                          <tr key={entry.id} className="border-b hover:bg-muted/50">
                            <td className="py-2 px-3 text-sm">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3 text-muted-foreground" />
                                {formatTimestamp(entry.completed_at)}
                              </div>
                            </td>
                            <td className="py-2 px-3 text-sm font-medium">{entry.checklist_name}</td>
                            <td className="py-2 px-3 text-sm">
                              <div className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {entry.completed_by}
                              </div>
                            </td>
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${entry.completion_pct === 100 ? 'bg-green-500' : 'bg-amber-500'}`}
                                    style={{ width: `${entry.completion_pct}%` }}
                                  />
                                </div>
                                <span className={`text-xs font-medium ${entry.completion_pct === 100 ? 'text-green-400' : 'text-amber-400'}`}>
                                  {entry.completion_pct}%
                                </span>
                              </div>
                            </td>
                            <td className="py-2 px-3 text-sm text-muted-foreground">
                              {entry.completed_items}/{entry.total_items}
                            </td>
                            <td className="py-2 px-3">
                              {entry.quality_score != null ? (
                                <div className="flex items-center gap-1">
                                  <Star className={`h-3 w-3 ${getScoreColor(entry.quality_score)}`} />
                                  <span className={`text-sm font-bold ${getScoreColor(entry.quality_score)}`}>{entry.quality_score}</span>
                                  <span className={`text-xs ${getScoreColor(entry.quality_score)}`}>({getScoreLabel(entry.quality_score)})</span>
                                </div>
                              ) : <span className="text-xs text-muted-foreground">-</span>}
                            </td>
                            <td className="py-2 px-3 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Timer className="h-3 w-3" />
                                {formatDuration(entry.duration_minutes)}
                              </div>
                            </td>
                            <td className="py-2 px-3 text-sm max-w-xs truncate text-muted-foreground" title={entry.notes || ''}>
                              {entry.notes || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Checklist Execution Modal */}
      <Dialog open={showChecklistModal} onOpenChange={setShowChecklistModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" />
              {selectedChecklist?.name}
            </DialogTitle>
          </DialogHeader>

          {selectedChecklist && (
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              {/* Progress */}
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                <div className="flex-1">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Progreso</span>
                    <span>{getChecklistProgress(selectedChecklist)}%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${getChecklistProgress(selectedChecklist)}%` }}
                    />
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  {Object.entries(checklistResults).filter(([k, v]) => v && selectedChecklist.items.some(i => i.id === k)).length} / {selectedChecklist.items.length}
                </div>
              </div>

              {/* Items grouped by category */}
              {(() => {
                const categories = [...new Set(selectedChecklist.items.map(i => i.category))];
                return categories.map(category => (
                  <div key={category} className="space-y-2">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                      {category}
                    </h4>
                    <div className="space-y-1">
                      {selectedChecklist.items
                        .filter(item => item.category === category)
                        .map((item, itemIndex) => {
                          const itemId = item.id || `fallback-${category}-${itemIndex}`;
                          return (
                            <div
                              key={itemId}
                              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                                checklistResults[itemId]
                                  ? 'bg-emerald-500/10 border border-emerald-500/30'
                                  : 'bg-muted/30 hover:bg-muted/50'
                              }`}
                              onClick={() => toggleChecklistItem(itemId)}
                            >
                              <Checkbox
                                checked={checklistResults[itemId] || false}
                                onCheckedChange={(e) => { e?.stopPropagation?.(); }}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className={`flex-1 text-sm ${checklistResults[itemId] ? 'line-through text-muted-foreground' : ''}`}>
                                {item.text}
                              </span>
                              {item.required && (
                                <Badge variant="outline" className="text-xs">Requerido</Badge>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ));
              })()}

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notas de la ejecucion (opcional)</Label>
                <Textarea
                  value={checklistNotes}
                  onChange={(e) => setChecklistNotes(e.target.value)}
                  placeholder="Observaciones, problemas encontrados, etc."
                  rows={3}
                  data-testid="checklist-notes"
                />
              </div>
            </div>
          )}

          <DialogFooter className="border-t pt-4">
            <div className="flex items-center justify-between w-full">
              <div className="text-xs text-muted-foreground">
                {selectedChecklist?.current_progress?.last_saved && (
                  <span>
                    Ultimo guardado: {formatTimestamp(selectedChecklist.current_progress.last_saved)}
                    {selectedChecklist.current_progress.saved_by && ` por ${selectedChecklist.current_progress.saved_by}`}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowChecklistModal(false)}>
                  Cancelar
                </Button>
                <Button variant="secondary" onClick={handleSaveProgress} data-testid="save-progress-btn">
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Progreso
                </Button>
                <Button
                  className="btn-primary"
                  onClick={handleCompleteChecklist}
                  disabled={
                    selectedChecklist?.items
                      .filter(i => i.required)
                      .some((i) => {
                        const itemId = i.id || `fallback-${i.category}-${selectedChecklist.items.filter(x => x.category === i.category).indexOf(i)}`;
                        return !checklistResults[itemId];
                      })
                  }
                  data-testid="complete-checklist-btn"
                >
                  <CheckSquare className="h-4 w-4 mr-2" />
                  Completar
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Checklist Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              Nuevo Checklist
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nombre *</Label>
                <Input
                  value={newChecklist.name}
                  onChange={(e) => setNewChecklist(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ej: Verificacion de Equipos Audiovisuales"
                  data-testid="new-checklist-name"
                />
              </div>
              <div className="col-span-2">
                <Label>Descripcion</Label>
                <Textarea
                  value={newChecklist.description}
                  onChange={(e) => setNewChecklist(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descripcion breve del proposito del checklist"
                  rows={2}
                />
              </div>
              <div>
                <Label>Frecuencia</Label>
                <Select value={newChecklist.frequency} onValueChange={(v) => setNewChecklist(prev => ({ ...prev, frequency: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Diario</SelectItem>
                    <SelectItem value="weekly">Semanal</SelectItem>
                    <SelectItem value="monthly">Mensual</SelectItem>
                    <SelectItem value="quarterly">Trimestral</SelectItem>
                    <SelectItem value="yearly">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Add Items */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-sm mb-3">Items del Checklist ({newChecklist.items.length})</h4>
              <div className="flex gap-2 mb-3">
                <Input
                  value={newItemCategory}
                  onChange={(e) => setNewItemCategory(e.target.value)}
                  placeholder="Categoria"
                  className="w-36"
                  data-testid="new-item-category"
                />
                <Input
                  value={newItemText}
                  onChange={(e) => setNewItemText(e.target.value)}
                  placeholder="Texto del item..."
                  className="flex-1"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItemToNewChecklist(); } }}
                  data-testid="new-item-text"
                />
                <div className="flex items-center gap-1">
                  <Checkbox
                    checked={newItemRequired}
                    onCheckedChange={(v) => setNewItemRequired(v)}
                  />
                  <span className="text-xs whitespace-nowrap">Req.</span>
                </div>
                <Button size="sm" onClick={addItemToNewChecklist} data-testid="add-item-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {newChecklist.items.length > 0 && (
                <div className="space-y-1 max-h-52 overflow-y-auto">
                  {(() => {
                    const cats = [...new Set(newChecklist.items.map(i => i.category))];
                    return cats.map(cat => (
                      <div key={cat}>
                        <p className="text-xs font-semibold text-muted-foreground uppercase mt-2 mb-1">{cat}</p>
                        {newChecklist.items.filter(i => i.category === cat).map((item, idx) => {
                          const globalIdx = newChecklist.items.indexOf(item);
                          return (
                            <div key={item.id} className="flex items-center gap-2 p-2 rounded bg-muted/30 text-sm">
                              <CheckSquare className="h-3 w-3 text-muted-foreground" />
                              <span className="flex-1">{item.text}</span>
                              {item.required && <Badge variant="outline" className="text-xs">Req</Badge>}
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeItemFromNewChecklist(globalIdx)}>
                                <Trash2 className="h-3 w-3 text-red-400" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
            <Button className="btn-primary" onClick={handleCreateChecklist} data-testid="save-new-checklist-btn">
              <Save className="h-4 w-4 mr-2" />
              Crear Checklist
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Completion Score Dialog */}
      <Dialog open={!!completionResult} onOpenChange={() => setCompletionResult(null)}>
        <DialogContent className="max-w-sm">
          <div className="text-center py-6">
            <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-4 ${getScoreBg(completionResult?.quality_score)}`}>
              <Trophy className={`h-10 w-10 ${getScoreColor(completionResult?.quality_score)}`} />
            </div>
            <h2 className="text-xl font-bold mb-1">Checklist Completado</h2>
            <p className="text-muted-foreground text-sm mb-4">Puntaje de Calidad</p>

            <div className={`text-5xl font-black mb-2 ${getScoreColor(completionResult?.quality_score)}`}>
              {completionResult?.quality_score ?? '-'}
            </div>
            <p className={`text-sm font-medium ${getScoreColor(completionResult?.quality_score)}`}>
              {getScoreLabel(completionResult?.quality_score)}
            </p>

            <div className="grid grid-cols-2 gap-3 mt-6 text-sm">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-muted-foreground text-xs">Items</p>
                <p className="font-bold">
                  {completionResult?.item_results ? Object.values(completionResult.item_results).filter(Boolean).length : 0}
                  <span className="text-muted-foreground font-normal"> completados</span>
                </p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-muted-foreground text-xs">Duracion</p>
                <p className="font-bold flex items-center justify-center gap-1">
                  <Timer className="h-3 w-3" />
                  {formatDuration(completionResult?.duration_minutes)}
                </p>
              </div>
            </div>

            <p className="text-xs text-muted-foreground mt-4">
              Puntaje = 70% items completados + 30% velocidad
            </p>

            <Button className="mt-4 btn-primary w-full" onClick={() => setCompletionResult(null)}>
              Continuar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestingPage;
